/**
 * Date String Utilities
 *
 * Funzioni per gestire date senza componente oraria (solo giorno/mese/anno).
 * Formato: YYYY-MM-DD (ISO 8601 calendar date)
 *
 * Queste funzioni garantiscono che le date siano indipendenti dal timezone,
 * evitando problemi quando client o server sono in fusi orari diversi.
 */
/** Regex per validare formato YYYY-MM-DD */
export const DATE_STRING_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Converte una Date JavaScript in stringa YYYY-MM-DD
 * Usa i metodi locali perché la data selezionata dal calendario è in locale
 *
 * @param date - Date object da convertire
 * @returns Stringa nel formato "YYYY-MM-DD"
 */
export function toDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Converte una Date UTC-midnight (es. prodotta da `parseDateString` o da
 * aritmetica su `Date.UTC`) in stringa YYYY-MM-DD leggendo i campi UTC.
 *
 * Da usare al posto di `toDateString` quando la Date rappresenta un
 * "giorno di calendario" ancorato a UTC: nei fusi a offset negativo la
 * lettura locale di `toDateString` slitterebbe al giorno precedente.
 */
export function toUTCDateString(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}
/**
 * Adds calendar months to a UTC date without rolling into the following month.
 * For example, 31 January + 1 month becomes 28/29 February.
 */
export function addUtcMonthsClamped(date, months) {
    const targetMonth = date.getUTCMonth() + months;
    const targetYear = date.getUTCFullYear() + Math.floor(targetMonth / 12);
    const normalizedMonth = ((targetMonth % 12) + 12) % 12;
    const lastDayOfTargetMonth = new Date(Date.UTC(targetYear, normalizedMonth + 1, 0)).getUTCDate();
    return new Date(Date.UTC(targetYear, normalizedMonth, Math.min(date.getUTCDate(), lastDayOfTargetMonth), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds()));
}
export function parseDateString(dateString, hours = 0, minutes = 0, seconds = 0, milliseconds = 0) {
    if (dateString === null)
        return null;
    if (dateString === undefined)
        return undefined;
    const datePart = dateString.split('T')[0].replace(/[+-]\d{2}:\d{2}$/, '');
    if (!DATE_STRING_REGEX.test(datePart)) {
        throw new Error(`Invalid date format: "${dateString}". Expected YYYY-MM-DD`);
    }
    const [year, month, day] = datePart.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds, milliseconds));
    if (date.getUTCFullYear() !== year ||
        date.getUTCMonth() !== month - 1 ||
        date.getUTCDate() !== day) {
        throw new Error(`Invalid calendar date: "${dateString}"`);
    }
    return date;
}
/**
 * Verifica se un valore e una stringa nel formato YYYY-MM-DD valido
 *
 * @param value - Valore da verificare
 * @returns true se e una stringa nel formato YYYY-MM-DD
 */
export function isValidDateString(value) {
    if (typeof value !== 'string' || !DATE_STRING_REGEX.test(value))
        return false;
    try {
        parseDateString(value);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Versione "safe" di parseDateString che non lancia eccezioni.
 * Utile per parsing di parametri URL o input utente non validati.
 *
 * @param dateString - Stringa da parsare
 * @returns Date se valido, undefined se null/undefined/invalido
 */
export function tryParseDateString(dateString) {
    if (!dateString || !isValidDateString(dateString)) {
        return undefined;
    }
    return parseDateString(dateString);
}
/**
 * Sposta un giorno di calendario di `days` giorni (anche negativi) senza
 * passare dal fuso del processo: `2026-02-28` + 1 → `2026-03-01`.
 */
export function addDaysToDateString(value, days) {
    const date = parseDateString(value);
    date.setUTCDate(date.getUTCDate() + days);
    return toUTCDateString(date);
}
/**
 * Giorno di calendario nel formato italiano `gg/mm/aaaa` (`2026-09-22` →
 * `22/09/2026`), per testi e documenti generati lato server: nessun fuso,
 * nessuna dipendenza da `Intl`.
 */
export function formatDateStringIt(value) {
    const [year, month, day] = value.split('-');
    return `${day}/${month}/${year}`;
}
//# sourceMappingURL=date-string.js.map